import { Link } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import RelatedLinks from "@/components/site/RelatedLinks";
import { AnswerBlock, Prose, ReviewStamp } from "@/components/site/ContentPrimitives";
import DecisionTree from "@/components/diagrams/DecisionTree";
import { useSEO, useJsonLd, breadcrumbSchema } from "@/lib/seo";
import { linkifyGoAndTrack } from "@/lib/goAndTrack";

const ROWS = [
  {
    dim: "Form factor",
    disp: "Thin, peel-and-stick label applied directly to shipment.",
    reus: "Hardware tracker — typically larger, requires mounting or pouches.",
  },
  {
    dim: "Lifecycle",
    disp: "Single-use. Discarded at end of journey.",
    reus: "Recovered, recharged, redeployed across many shipments.",
  },
  {
    dim: "CapEx model",
    disp: "Per-shipment OpEx. No upfront fleet purchase.",
    reus: "CapEx-heavy. Hardware fleet must be procured upfront.",
  },
  {
    dim: "Reverse logistics",
    disp: "None. Label travels with the shipment, no recovery required.",
    reus: "Required. Trackers must come back from end customers, partners, or returns.",
  },
  {
    dim: "Operational overhead",
    disp: "Apply, dispatch, done.",
    reus: "Pairing, charging, recovery, redistribution, write-offs.",
  },
  {
    dim: "Best-fit lane",
    disp: "Single-trip, partner-heavy, customer-terminating, or pilot lanes.",
    reus: "High-frequency, closed-loop lanes where recovery is reliable.",
  },
  {
    dim: "Time to deploy",
    disp: "Days. No infrastructure standup.",
    reus: "Weeks-to-months. Procurement, distribution, and process design.",
  },
  {
    dim: "Exposure to loss",
    disp: "Low. Single-use cost is contained per shipment.",
    reus: "Higher. Lost or stuck trackers carry hardware-replacement cost.",
  },
  {
    dim: "Platform layer",
    disp: "GoAndTrack — live tracking, alerts, exception workflows.",
    reus: "Vendor-specific platform tied to the hardware estate.",
  },
];

export default function VsReusableTrackers() {
  useSEO({
    title: "Disposable vs Reusable Shipment Trackers",
    description: "Objective comparison of disposable smart labels and reusable GPS trackers — lifecycle, cost, deployment, recovery, and best-fit shipment lanes.",
    canonical: "/disposable-vs-reusable-trackers",
  });

  useJsonLd(
    "breadcrumb-vs",
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Disposable Package Tracker", url: "/disposable-package-tracker" },
      { name: "Disposable vs Reusable" },
    ]),
  );

  return (
    <div data-testid="page-vs">
      <PageHero
        eyebrow="Comparison"
        title="Disposable vs Reusable Shipment Trackers"
        subtitle="A dimension-by-dimension view — with an honest section on when reusable is the better choice. Category authority means acknowledging where a technology does not fit."
      >
        <Link to="/request-a-trial" className="btn-primary" data-testid="page-cta-trial">
          Speak to an expert <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHero>

      <section className="section">
        <div className="container-x">
          <Prose>
            <AnswerBlock>
              Disposable trackers are optimised for one-way and
              low-recovery lanes; reusable trackers are optimised for
              closed-loop networks with dependable device recovery. The
              honest answer for most operations is a mix — reusable on
              backbone lanes with reliable recovery, disposable on the
              first or last leg where hardware would disappear.
            </AnswerBlock>
          </Prose>
        </div>
      </section>

      <section className="section">
        <div className="container-x">
          <div className="overflow-x-auto border border-line">
            <table className="w-full text-left" data-testid="comparison-table">
              <thead>
                <tr className="bg-surface-subtle border-b border-line">
                  <th className="px-6 py-5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-subtle w-1/4">
                    Dimension
                  </th>
                  <th className="px-6 py-5 font-mono text-[11px] uppercase tracking-[0.18em] text-brand">
                    Disposable smart labels
                  </th>
                  <th className="px-6 py-5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-subtle">
                    Reusable trackers
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r, i) => (
                  <tr
                    key={r.dim}
                    className={i % 2 === 0 ? "bg-white" : "bg-surface-subtle"}
                    data-testid={`compare-row-${i}`}
                  >
                    <td className="px-6 py-5 align-top text-sm font-medium text-ink border-b border-line">
                      {r.dim}
                    </td>
                    <td className="px-6 py-5 align-top text-sm text-ink-muted border-b border-line">
                      <span className="inline-flex items-start gap-2">
                        <Check className="h-4 w-4 mt-0.5 text-brand shrink-0" />
                        {linkifyGoAndTrack(r.disp)}
                      </span>
                    </td>
                    <td className="px-6 py-5 align-top text-sm text-ink-muted border-b border-line">
                      <span className="inline-flex items-start gap-2">
                        <X className="h-4 w-4 mt-0.5 text-ink-subtle shrink-0" />
                        {r.reus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="section bg-surface-subtle border-y border-line">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <p className="eyebrow mb-4">Choose disposable when</p>
            <ul className="space-y-3 text-[15px] text-ink-muted">
              <li>· The shipment ends at a customer or partner site.</li>
              <li>· Recovery of reusable hardware is unreliable or impractical.</li>
              <li>· You&apos;re piloting a lane, partner, or programme.</li>
              <li>· Finance prefers OpEx visibility cost over CapEx fleet.</li>
              <li>· Lane volumes are inconsistent or seasonal.</li>
            </ul>
          </div>
          <div>
            <p className="eyebrow mb-4">Choose reusable when</p>
            <ul className="space-y-3 text-[15px] text-ink-muted">
              <li>· You operate fully closed-loop lanes (e.g., asset returns).</li>
              <li>· Recovery rates are reliably above 95%.</li>
              <li>· You have the operational capacity to manage a hardware fleet.</li>
              <li>· Per-shipment volume is high enough to justify amortisation.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section border-t border-line">
        <div className="container-x">
          <Prose>
            <h2 className="h2">When reusable is genuinely the better choice</h2>
            <AnswerBlock>
              Closed-loop logistics, captive fleets, high device recovery
              rates, and long-duration lanes that benefit from
              rechargeable hardware. If a programme reliably recovers
              &gt;90% of devices trip after trip, reusable typically wins
              on total cost per shipment tracked.
            </AnswerBlock>
            <ul className="mt-4 space-y-2 body-text list-disc pl-6">
              <li>
                <strong className="text-ink">Closed-loop networks.</strong>{" "}
                Trackers move between fixed sites the operator controls.
              </li>
              <li>
                <strong className="text-ink">Captive fleets.</strong>{" "}
                Own vehicles or own containers, where the operator can
                enforce recovery.
              </li>
              <li>
                <strong className="text-ink">Long-duration lanes.</strong>{" "}
                Multi-week or ocean shipments where rechargeable batteries
                pay off.
              </li>
              <li>
                <strong className="text-ink">Existing device management.</strong>{" "}
                An operations team already runs charging, provisioning,
                and recovery for hardware.
              </li>
              <li>
                <strong className="text-ink">High per-lane volume.</strong>{" "}
                Enough trips per device to fully amortise the CapEx.
              </li>
            </ul>

            <h2 className="h2 mt-14">Hybrid is normal</h2>
            <p className="body-text mt-3">
              In practice most operations run a hybrid: reusable trackers
              on high-volume backbone lanes; disposable smart labels on
              first mile, last mile, one-way flows, high-value spot
              shipments, returns, and pilots. This isn&apos;t a compromise
              — it&apos;s the shape mature programmes tend to take once
              the recovery data is real.
            </p>

            <h2 className="h2 mt-14">Decision tree</h2>
            <DecisionTree />

            <ReviewStamp
              date="February 2026"
              basis="Direct deployment experience across UK/EU disposable and reusable tracking programmes."
            />
          </Prose>
        </div>
      </section>

      <RelatedLinks
        title="Related reading"
        links={[
          { to: "/disposable-package-tracker", label: "Disposable Package Tracker — complete category guide" },
          { to: "/disposable-tracking-cost", label: "How Much Does Disposable Shipment Tracking Cost?" },
          { to: "/gps-vs-ble-vs-cellular", label: "GPS vs BLE vs Cellular — technology comparison" },
          { to: "/smart-labels-for-high-value-shipments", label: "Smart Labels for High-Value Shipments" },
          { to: "/smart-labels-for-pilot-projects", label: "Smart Labels for Pilot Projects" },
        ]}
      />

      <CTAStrip
        title="Not sure which approach fits your lane?"
        body="A specialist will run through your shipment profile and recommend the right model — including hybrid setups."
      />
    </div>
  );
}
